/* ---------------------------------------------------------------------------------------------------------------------
   CUSTOM SEARCH
   ---------------------------------------------------------------------------------------------------------------------
   Drives the whole search experience:

   - opens the panel as a fullscreen overlay on every viewport
   - queries Shopify's Predictive Search API and injects the rendered "predictive-search" section
   - switches result tabs
   - records visited products so the "recently viewed" section on /search has something to show

   The panel markup lives in sections/search-bar.liquid, the result markup in sections/predictive-search.liquid.
--------------------------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  var DEBOUNCE_DELAY = 300;
  var MIN_QUERY_LENGTH = 2;
  var RESULT_SECTION_ID = 'predictive-search';
  var RECENTLY_VIEWED_KEY = 'theme:recently-viewed-products';
  var RECENTLY_VIEWED_LIMIT = 5;

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function debounce(fn, wait) {
    var timer = null;

    return function () {
      var args = arguments;
      var context = this;

      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* -------------------------------------------------------------------------------------------------------------------
     PANEL
  ------------------------------------------------------------------------------------------------------------------- */

  function CustomSearch(root) {
    this.root = root;
    this.panel = root.querySelector('[data-custom-search-panel]');
    this.input = root.querySelector('[data-custom-search-input]');
    this.clearButton = root.querySelector('[data-custom-search-clear]');
    this.discovery = root.querySelector('[data-custom-search-discovery]');
    this.results = root.querySelector('[data-custom-search-results]');
    this.loading = root.querySelector('[data-custom-search-loading]');

    if (!this.panel || !this.input || !this.results) return;

    this.isOpen = false;
    this.controller = null;
    this.lastQuery = '';
    this.header = document.querySelector('store-header');

    this.onInput = debounce(this.search.bind(this), DEBOUNCE_DELAY);

    this.bind();
  }

  CustomSearch.prototype.bind = function () {
    var self = this;

    // Every trigger opens the same panel. focus() has to happen inside the click handler
    // itself (not after an await) or iOS refuses to open the keyboard.
    // Bound on document because the triggers live in the header, outside this element.
    this.onDocumentClick = function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var trigger = event.target.closest('[data-custom-search-open]');
      if (!trigger) return;

      // The hero field on /search is a trigger that contains a real submit button —
      // that button keeps its native job.
      if (event.target.closest('button[type="submit"]')) return;

      event.preventDefault();

      // A trigger that is (or contains) a search input hands its value over, so the panel
      // reopens with the current query and its live results instead of the discovery state.
      var prefill = null;
      if (trigger.tagName === 'INPUT') {
        prefill = trigger.value;
      } else {
        var innerInput = trigger.querySelector('input[type="search"]');
        if (innerInput) prefill = innerInput.value;
      }

      self.open(prefill);
    };

    this.onDocumentKeydown = function (event) {
      if (event.key === 'Escape' && self.isOpen) self.close();
    };

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);

    this.root.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      if (event.target.closest('[data-custom-search-close]')) {
        event.preventDefault();
        self.close();
      }

      if (event.target.closest('[data-custom-search-clear]')) {
        event.preventDefault();
        self.input.value = '';
        self.input.focus();
        self.toggleClearButton();
        self.showDiscovery();
      }
    });

    this.input.addEventListener('input', function () {
      self.toggleClearButton();
      self.onInput();
    });

    // Tabs live inside injected markup, so they are handled by delegation
    this.results.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var tab = event.target.closest('[data-custom-search-tab]');
      if (!tab) return;

      event.preventDefault();
      self.selectTab(tab.getAttribute('data-custom-search-tab'));
    });

    /* ---------------------------------------------------------------------------------------------------------------
       NAVIGATION BOOST
       Clicking a result is a full page navigation and the fullscreen panel would sit frozen
       until the next page paints. Two measures, both delegated on the panel:
       - feedback: pressed state on the clicked link + an indeterminate progress bar
       - prefetch: as soon as a link is touched, hovered or focused, its target is prerendered
         (Chrome, via Speculation Rules) or prefetched (rel=prefetch fallback)
    --------------------------------------------------------------------------------------------------------------- */

    this.progress = this.panel.querySelector('[data-custom-search-progress]');
    this.prefetchedUrls = {};
    this.prerenderCount = 0;

    this.panel.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var link = event.target.closest('a[href]');
      if (!link || !self.isPlainNavigation(event, link)) return;

      link.classList.add('custom-search-navigating');
      if (self.progress) self.progress.hidden = false;
    });

    var onLinkIntent = function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var link = event.target.closest('a[href]');
      if (link) self.prefetch(link);
    };

    this.panel.addEventListener('touchstart', onLinkIntent, { passive: true });
    this.panel.addEventListener('mouseover', onLinkIntent);
    this.panel.addEventListener('focusin', onLinkIntent);

    // Returning via the back button restores the page from the back/forward cache exactly as
    // it was left — including a running progress bar. Reset the frozen loading state.
    this.onPageShow = function () {
      if (self.progress) self.progress.hidden = true;

      Array.prototype.forEach.call(self.panel.querySelectorAll('.custom-search-navigating'), function (el) {
        el.classList.remove('custom-search-navigating');
      });
    };

    window.addEventListener('pageshow', this.onPageShow);
  };

  // Only decorate clicks that really navigate this tab: primary button, no modifier keys,
  // no _blank target, same-origin http(s) destination.
  CustomSearch.prototype.isPlainNavigation = function (event, link) {
    if (event.defaultPrevented) return false;
    if (typeof event.button === 'number' && event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    return this.isSameOriginLink(link);
  };

  CustomSearch.prototype.isSameOriginLink = function (link) {
    var href = link.getAttribute('href') || '';
    if (href === '' || href.charAt(0) === '#') return false;
    return link.origin === window.location.origin;
  };

  CustomSearch.prototype.prefetch = function (link) {
    if (!this.isSameOriginLink(link)) return;
    // Respect data saver mode — speculative loading is pure overhead there
    if (navigator.connection && navigator.connection.saveData) return;

    var url = link.href;
    if (this.prefetchedUrls[url]) return;
    this.prefetchedUrls[url] = true;

    var supportsPrerender =
      window.HTMLScriptElement &&
      typeof HTMLScriptElement.supports === 'function' &&
      HTMLScriptElement.supports('speculationrules');

    // Chrome caps concurrent prerenders, so only the first few intents get the full
    // prerender; everything after falls back to a plain prefetch.
    if (supportsPrerender && this.prerenderCount < 6) {
      this.prerenderCount += 1;
      var rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({ prerender: [{ urls: [url] }] });
      document.head.appendChild(rules);
    } else {
      var hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = url;
      hint.as = 'document';
      document.head.appendChild(hint);
    }
  };

  // The customizer re-renders sections; tear the old listeners down before rebinding
  CustomSearch.prototype.destroy = function () {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('pageshow', this.onPageShow);

    document.documentElement.classList.remove('custom-search-is-open');
    document.documentElement.style.paddingRight = '';
    if (this.header) this.header.classList.remove('search-open');
  };

  CustomSearch.prototype.open = function (prefill) {
    if (this.isOpen) {
      this.close();
      return;
    }

    if (typeof prefill === 'string' && prefill.trim() !== '') {
      this.input.value = prefill;
    }

    this.isOpen = true;
    this.panel.hidden = false;

    // The scroll lock removes a classic scrollbar and would shift the whole page sideways.
    // Reserve its width as padding before locking; on overlay scrollbars this is 0.
    var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.paddingRight = scrollbarWidth + 'px';
    }

    document.documentElement.classList.add('custom-search-is-open');
    if (this.header) this.header.classList.add('search-open');

    // Synchronous focus — required for the iOS keyboard to appear
    this.input.focus();

    if (this.input.value.trim().length >= MIN_QUERY_LENGTH) {
      this.search();
    } else {
      this.showDiscovery();
    }

    this.toggleClearButton();
  };

  CustomSearch.prototype.close = function () {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.panel.hidden = true;

    document.documentElement.classList.remove('custom-search-is-open');
    document.documentElement.style.paddingRight = '';
    if (this.header) this.header.classList.remove('search-open');

    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }

    this.input.blur();
  };

  CustomSearch.prototype.toggleClearButton = function () {
    if (!this.clearButton) return;
    this.clearButton.hidden = this.input.value.length === 0;
  };

  CustomSearch.prototype.showDiscovery = function () {
    if (this.discovery) this.discovery.hidden = false;
    this.results.hidden = true;
    if (this.loading) this.loading.hidden = true;
    this.lastQuery = '';
  };

  CustomSearch.prototype.showLoading = function () {
    if (this.discovery) this.discovery.hidden = true;
    this.results.hidden = true;
    if (this.loading) this.loading.hidden = false;
  };

  CustomSearch.prototype.showResults = function () {
    if (this.discovery) this.discovery.hidden = true;
    if (this.loading) this.loading.hidden = true;
    this.results.hidden = false;
  };

  CustomSearch.prototype.search = function () {
    var self = this;
    var query = this.input.value.trim();

    if (query.length < MIN_QUERY_LENGTH) {
      this.showDiscovery();
      return;
    }

    if (query === this.lastQuery && !this.results.hidden) return;
    this.lastQuery = query;

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    this.showLoading();

    // limit_scope=each is essential: Shopify's default is "all", which spreads the limit
    // across every resource type combined and starves the product results.
    var url =
      rootUrl() +
      'search/suggest?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product,collection,article,query' +
      '&resources[limit]=8' +
      '&resources[limit_scope]=each' +
      '&section_id=' +
      RESULT_SECTION_ID;

    fetch(url, { signal: this.controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fragment = parsed.querySelector('[data-custom-search-results-inner]');

        self.results.innerHTML = fragment ? fragment.innerHTML : '';
        self.showResults();
        self.selectFirstTab();
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        // Fall back to the discovery state rather than leaving an empty panel behind
        self.showDiscovery();
      });
  };

  CustomSearch.prototype.selectFirstTab = function () {
    var firstTab = this.results.querySelector('[data-custom-search-tab]');
    if (firstTab) this.selectTab(firstTab.getAttribute('data-custom-search-tab'));
  };

  CustomSearch.prototype.selectTab = function (name) {
    var tabs = this.results.querySelectorAll('[data-custom-search-tab]');
    var panels = this.results.querySelectorAll('[data-custom-search-tabpanel]');

    Array.prototype.forEach.call(tabs, function (tab) {
      var selected = tab.getAttribute('data-custom-search-tab') === name;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    });

    Array.prototype.forEach.call(panels, function (panel) {
      panel.hidden = panel.getAttribute('data-custom-search-tabpanel') !== name;
    });
  };

  /* -------------------------------------------------------------------------------------------------------------------
     SEARCH RESULTS PAGE TABS
  ------------------------------------------------------------------------------------------------------------------- */

  function initPageTabs() {
    var container = document.querySelector('[data-custom-search-page-tabs]');
    if (!container || container.customSearchBound) return;
    container.customSearchBound = true;

    container.addEventListener('click', function (event) {
      if (!event.target || typeof event.target.closest !== 'function') return;

      var tab = event.target.closest('[data-custom-search-page-tab]');
      if (!tab) return;

      event.preventDefault();
      var name = tab.getAttribute('data-custom-search-page-tab');

      Array.prototype.forEach.call(container.querySelectorAll('[data-custom-search-page-tab]'), function (item) {
        item.setAttribute('aria-selected', item === tab ? 'true' : 'false');
      });

      Array.prototype.forEach.call(document.querySelectorAll('[data-custom-search-page-panel]'), function (panel) {
        panel.hidden = panel.getAttribute('data-custom-search-page-panel') !== name;
      });
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     RECENTLY VIEWED TRACKER
     ---------------------------------------------------------------------------------------------------------------------
     The theme reads "theme:recently-viewed-products" but never writes it, so we record visited
     products here. Ids are stored newest first and capped, matching what the theme's
     <recently-viewed-products> element expects.
  ------------------------------------------------------------------------------------------------------------------- */

  function trackRecentlyViewed() {
    var id = window.customSearchCurrentProductId;
    if (!id) return;

    try {
      var stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
      if (!Array.isArray(stored)) stored = [];

      var next = [id].concat(
        stored.filter(function (item) {
          return item !== id;
        })
      );

      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next.slice(0, RECENTLY_VIEWED_LIMIT)));
    } catch (error) {
      // Private mode or a full quota — recently viewed is optional, so fail quietly
    }
  }

  /* -------------------------------------------------------------------------------------------------------------------
     BOOT
  ------------------------------------------------------------------------------------------------------------------- */

  var activeSearch = null;

  function initSearch() {
    var root = document.querySelector('[data-custom-search]');
    if (!root) return;

    // Guard against binding the same markup twice — otherwise a second document click
    // listener would open and immediately close the panel again.
    if (activeSearch) {
      if (activeSearch.root === root) return;
      activeSearch.destroy();
    }

    activeSearch = new CustomSearch(root);
  }

  function init() {
    initSearch();
    initPageTabs();
    trackRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme customizer re-renders sections, so rebind when that happens
  document.addEventListener('shopify:section:load', function () {
    initSearch();
    initPageTabs();
  });
})();
